/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState, useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  flexRender,
} from "@tanstack/react-table";
import { APIContext } from "../../api";
import { Link, useNavigate } from "react-router-dom";
import HelmetLayout from "../../components/HelmetLayout";
import { PulseLoader } from "react-spinners";
import DateDisplay from "../../components/ui-kit/date-display";
import { toast } from "react-toastify";
import {
  Grid,
  Search,
  CheckCircle2,
  Clock,
  Star,
  MessageSquare,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Layers,
} from "lucide-react";

export default function Apps() {
  const [activeTab, setActiveTab] = useState("apps"); // "apps" | "reviews"
  const [appsList, setAppsList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [byApp, setByApp] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [actioningId, setActioningId] = useState(null);
  const [unapprovingId, setUnapprovingId] = useState(null);

  const {
    getAppsList,
    getPendingAppReviews,
    approveRejectAppReview,
    unapproveApp,
  } = useContext(APIContext);

  const [statusFilter, setStatusFilter] = useState("");
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState([{ id: "created_at", desc: true }]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 20 });
  const navigate = useNavigate();

  const fetchApps = () => {
    setIsLoading(true);
    getAppsList(statusFilter)
      .then((apps) => setAppsList(Array.isArray(apps) ? apps : []))
      .catch((err) => {
        console.error("Error fetching apps:", err);
        toast.error("Could not load apps list");
      })
      .finally(() => setIsLoading(false));
  };

  const fetchReviews = () => {
    setReviewsLoading(true);
    getPendingAppReviews()
      .then((list) => setByApp(Array.isArray(list) ? list : []))
      .catch((err) => {
        console.error("Error fetching pending app reviews:", err);
        toast.error("Could not load app reviews");
      })
      .finally(() => setReviewsLoading(false));
  };

  useEffect(() => {
    fetchApps();
  }, [statusFilter]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "reviews") {
      fetchReviews();
    }
  };

  const handleApproveReject = (reviewId, status) => {
    setActioningId(reviewId);
    approveRejectAppReview(reviewId, status)
      .then(() => {
        toast.success(
          status === "approved" ? "Review approved" : "Review rejected"
        );
        fetchReviews();
      })
      .catch((err) => {
        toast.error(err?.response?.data?.error || "Failed to update review");
      })
      .finally(() => setActioningId(null));
  };

  const handleUnapprove = (e, app) => {
    e.stopPropagation();
    if (
      !window.confirm(
        `Remove "${app.app_name}" from the Grigora app store? The app will move to development and the developer will be notified by email.`
      )
    )
      return;
    setUnapprovingId(app.id);
    unapproveApp(app.id)
      .then(() => {
        toast.success(
          "App has been removed from the app store and developer has been notified."
        );
        fetchApps();
      })
      .catch((err) => {
        toast.error(err?.response?.data?.error || "Failed to unapprove app");
      })
      .finally(() => setUnapprovingId(null));
  };

  const columns = useMemo(
    () => [
      {
        id: "app_name",
        header: "App Name",
        accessorKey: "app_name",
        cell: (info) => {
          const app = info.row.original;
          return (
            <div className="flex items-center gap-3">
              {app.icon_url ? (
                <img
                  src={app.icon_url}
                  className="w-9 h-9 rounded-lg object-cover border border-slate-200 shadow-sm shrink-0"
                  alt="icon"
                />
              ) : (
                <div className="w-9 h-9 bg-slate-100 border border-slate-200 rounded-lg flex items-center justify-center text-xs text-slate-500 font-semibold shrink-0">
                  {app.app_name ? app.app_name[0].toUpperCase() : "A"}
                </div>
              )}
              <div className="min-w-0">
                <span className="font-semibold text-slate-900 block truncate hover:text-[#623ce7] transition-colors">
                  {app.app_name}
                </span>
                <span className="text-[11px] font-mono text-slate-400 block truncate">
                  ID: {String(app.id || "")}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        id: "developer",
        header: "Developer",
        accessorKey: "developer_name",
        cell: (info) => (
          <span className="text-slate-600 font-medium">
            {info.getValue() || "—"}
          </span>
        ),
      },
      {
        id: "status",
        header: "Status",
        accessorKey: "status",
        cell: (info) => {
          const status = info.getValue();
          const reviewStatus = info.row.original.review_status;

          let badgeClass = "bg-slate-100 text-slate-600 border-slate-200";
          let label = status;

          if (reviewStatus === "pending") {
            badgeClass = "bg-amber-50 text-amber-700 border-amber-200";
            label = "Pending Review";
          } else if (reviewStatus === "rejected") {
            badgeClass = "bg-rose-50 text-rose-700 border-rose-200";
            label = "Rejected";
          } else if (status === "production" || reviewStatus === "approved") {
            badgeClass = "bg-emerald-50 text-emerald-700 border-emerald-200";
            label = "Approved / Live";
          } else if (status === "development") {
            badgeClass = "bg-blue-50 text-blue-700 border-blue-200";
            label = "Development";
          }

          return (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${badgeClass}`}
            >
              {label}
            </span>
          );
        },
      },
      {
        id: "featured",
        header: "Featured",
        cell: (info) => {
          const app = info.row.original;
          const featured = app.featured_app === true;
          const idx = app.featured_index;
          if (!featured) return <span className="text-slate-300">—</span>;
          return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
              <Star size={11} className="fill-amber-400 text-amber-500" />
              {idx != null && idx !== "" ? `#${idx}` : "Featured"}
            </span>
          );
        },
      },
      {
        id: "created_at",
        header: "Created",
        accessorKey: "created_at",
        cell: (info) => {
          const date = info.getValue() ? new Date(info.getValue()) : null;
          return date ? (
            <span className="text-slate-500 text-xs">
              <DateDisplay date={date} />
            </span>
          ) : (
            <span className="text-slate-300">—</span>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        cell: (info) => {
          const app = info.row.original;
          const isApproved =
            app.status === "production" || app.review_status === "approved";
          if (!isApproved) return <span className="text-slate-300">—</span>;
          return (
            <button
              type="button"
              disabled={unapprovingId === app.id}
              onClick={(e) => handleUnapprove(e, app)}
              className="text-xs px-2.5 py-1 text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg transition-colors font-medium disabled:opacity-50"
            >
              {unapprovingId === app.id ? "Unapproving..." : "Unapprove"}
            </button>
          );
        },
      },
    ],
    [unapprovingId]
  );

  const table = useReactTable({
    data: appsList,
    columns,
    state: { sorting, pagination, globalFilter },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const filteredTotal = table.getPrePaginationRowModel().rows.length;
  const pendingCount = useMemo(
    () => appsList.filter((a) => a.review_status === "pending").length,
    [appsList]
  );
  const approvedCount = useMemo(
    () =>
      appsList.filter(
        (a) => a.status === "production" || a.review_status === "approved"
      ).length,
    [appsList]
  );
  const totalPendingReviews = useMemo(
    () =>
      byApp.reduce((acc, app) => acc + (app.reviews?.length || 0), 0),
    [byApp]
  );

  return (
    <>
      <HelmetLayout title="App Management" />
      <div className="space-y-6 pb-12">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">App Management</h1>
            <p className="text-slate-500 text-sm mt-0.5">
              Manage Grigora app store apps, developer submissions, and review moderation.
            </p>
          </div>

          {/* Tab Switcher */}
          <div className="flex items-center gap-1.5 p-1 bg-slate-100 rounded-xl w-fit">
            <button
              onClick={() => handleTabChange("apps")}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                activeTab === "apps"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Apps ({appsList.length})
            </button>
            <button
              onClick={() => handleTabChange("reviews")}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                activeTab === "reviews"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              App Reviews
              {totalPendingReviews > 0 && (
                <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-amber-100 text-amber-800">
                  {totalPendingReviews}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Tab 1: Apps Table */}
        {activeTab === "apps" && (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500 mb-1">Total Apps</p>
                  <h3 className="text-2xl font-bold text-slate-900">
                    {isLoading ? "—" : appsList.length}
                  </h3>
                </div>
                <div className="p-3 bg-[#623ce7]/10 text-[#623ce7] rounded-xl">
                  <Grid size={22} />
                </div>
              </div>
              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500 mb-1">Pending Approval</p>
                  <h3 className="text-2xl font-bold text-amber-600">
                    {isLoading ? "—" : pendingCount}
                  </h3>
                </div>
                <div className="p-3 bg-amber-50 text-amber-600 rounded-xl">
                  <Clock size={22} />
                </div>
              </div>
              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500 mb-1">Approved & Live</p>
                  <h3 className="text-2xl font-bold text-emerald-600">
                    {isLoading ? "—" : approvedCount}
                  </h3>
                </div>
                <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl">
                  <CheckCircle2 size={22} />
                </div>
              </div>
            </div>

            {/* Filter & Search Bar */}
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col md:flex-row gap-4 justify-between items-stretch md:items-center">
              <div className="relative flex-1 max-w-md">
                <Search
                  size={18}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  type="text"
                  placeholder="Search apps by name, ID or developer..."
                  value={globalFilter ?? ""}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#623ce7] focus:ring-1 focus:ring-[#623ce7] transition-colors"
                />
              </div>

              <div className="flex items-center gap-3">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3.5 py-2 border border-slate-200 rounded-lg text-sm text-slate-700 bg-white focus:outline-none focus:border-[#623ce7] focus:ring-1 focus:ring-[#623ce7]"
                >
                  <option value="">All Statuses</option>
                  <option value="pending">Pending Review</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="development">In Development</option>
                </select>
              </div>
            </div>

            {/* Apps Table */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 text-xs uppercase tracking-wider font-semibold">
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <th
                            key={header.id}
                            className="px-6 py-3.5 cursor-pointer select-none hover:bg-slate-100 transition-colors"
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            <div className="flex items-center gap-2">
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              <span className="text-[10px] text-slate-400">
                                {header.column.getIsSorted() === "asc"
                                  ? "▲"
                                  : header.column.getIsSorted() === "desc"
                                  ? "▼"
                                  : ""}
                              </span>
                            </div>
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {isLoading ? (
                      <tr>
                        <td
                          colSpan={columns.length}
                          className="py-16 text-center"
                        >
                          <PulseLoader size={10} color="#623ce7" />
                          <p className="text-slate-400 text-xs mt-3">
                            Loading apps list...
                          </p>
                        </td>
                      </tr>
                    ) : table.getRowModel().rows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={columns.length}
                          className="py-16 text-center text-slate-500"
                        >
                          <Grid size={32} className="mx-auto text-slate-300 mb-2" />
                          <p className="font-semibold text-slate-700">No apps found</p>
                          <p className="text-xs text-slate-400 mt-0.5">
                            Try adjusting your filter or search criteria.
                          </p>
                        </td>
                      </tr>
                    ) : (
                      table.getRowModel().rows.map((row) => (
                        <tr
                          key={row.id}
                          onClick={() => navigate(`/apps/${row.original.id}`)}
                          className="hover:bg-slate-50/80 cursor-pointer transition-colors"
                        >
                          {row.getVisibleCells().map((cell) => (
                            <td key={cell.id} className="px-6 py-4 whitespace-nowrap">
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </td>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination Bar */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50/50 text-xs text-slate-500">
                <span>
                  Showing{" "}
                  <strong className="text-slate-700 font-semibold">
                    {filteredTotal === 0
                      ? 0
                      : pagination.pageIndex * pagination.pageSize + 1}
                  </strong>{" "}
                  to{" "}
                  <strong className="text-slate-700 font-semibold">
                    {Math.min(
                      (pagination.pageIndex + 1) * pagination.pageSize,
                      filteredTotal
                    )}
                  </strong>{" "}
                  of{" "}
                  <strong className="text-slate-700 font-semibold">
                    {filteredTotal}
                  </strong>{" "}
                  apps
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                    className="p-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <span className="text-slate-600 font-medium px-2">
                    Page {table.getState().pagination.pageIndex + 1} of{" "}
                    {table.getPageCount() || 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                    className="p-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Tab 2: App Reviews Moderation */}
        {activeTab === "reviews" && (
          <>
            {/* Reviews KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500 mb-1">
                    Pending Reviews
                  </p>
                  <h3 className="text-2xl font-bold text-amber-600">
                    {reviewsLoading ? "—" : totalPendingReviews}
                  </h3>
                </div>
                <div className="p-3 bg-amber-50 text-amber-600 rounded-xl">
                  <MessageSquare size={22} />
                </div>
              </div>
              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500 mb-1">
                    Apps with Pending Reviews
                  </p>
                  <h3 className="text-2xl font-bold text-slate-900">
                    {reviewsLoading ? "—" : byApp.length}
                  </h3>
                </div>
                <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
                  <Layers size={22} />
                </div>
              </div>
            </div>

            {/* Reviews Content */}
            {reviewsLoading ? (
              <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-slate-200">
                <PulseLoader size={12} color="#623ce7" />
                <p className="text-slate-400 text-sm mt-4">Loading pending reviews...</p>
              </div>
            ) : byApp.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl border border-slate-200 shadow-sm">
                <CheckCircle2 size={36} className="mx-auto text-emerald-500 mb-3" />
                <h3 className="text-base font-semibold text-slate-800 mb-1">
                  All reviews moderated!
                </h3>
                <p className="text-slate-500 text-sm max-w-sm mx-auto">
                  There are no pending user reviews to moderate at this time.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {byApp.map((app) => (
                  <div
                    key={app.app_id}
                    className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
                  >
                    {/* App Header */}
                    <div className="p-4 sm:p-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-slate-50/50">
                      <div className="flex items-center gap-3.5">
                        {app.icon_url ? (
                          <img
                            src={app.icon_url}
                            alt=""
                            className="w-12 h-12 rounded-xl object-cover border border-slate-200 shadow-sm"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-xl bg-slate-200 text-slate-600 flex items-center justify-center font-bold text-base">
                            {app.app_name ? app.app_name[0].toUpperCase() : "A"}
                          </div>
                        )}
                        <div>
                          <h3 className="text-base font-bold text-slate-900">
                            {app.app_name}
                          </h3>
                          <p className="text-xs font-mono text-slate-400">
                            ID: {app.app_id}
                          </p>
                        </div>
                      </div>
                      <Link
                        to={`/apps/${app.app_id}`}
                        className="px-3 py-1.5 text-xs font-semibold text-[#623ce7] bg-[#623ce7]/10 hover:bg-[#623ce7]/20 rounded-lg transition-colors flex items-center gap-1.5 w-fit"
                      >
                        <ExternalLink size={13} />
                        View App Details
                      </Link>
                    </div>

                    {/* App Reviews Table */}
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-sm border-collapse">
                        <thead className="bg-slate-50/70 border-b border-slate-100 text-slate-500 text-xs uppercase font-semibold">
                          <tr>
                            <th className="px-5 py-3">Reviewer</th>
                            <th className="px-5 py-3">Rating</th>
                            <th className="px-5 py-3">Comment</th>
                            <th className="px-5 py-3">Date</th>
                            <th className="px-5 py-3 text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {(app.reviews || []).map((review) => (
                            <tr key={review.id} className="hover:bg-slate-50/50">
                              <td className="px-5 py-3.5">
                                <div className="flex items-center gap-2.5">
                                  {review.reviewer_photo_url ? (
                                    <img
                                      src={review.reviewer_photo_url}
                                      alt=""
                                      className="w-8 h-8 rounded-full object-cover border border-slate-200"
                                    />
                                  ) : (
                                    <div className="w-8 h-8 rounded-full bg-slate-200 text-slate-600 font-bold text-xs flex items-center justify-center">
                                      {(
                                        review.reviewer_name ||
                                        review.reviewer_username ||
                                        "?"
                                      )[0].toUpperCase()}
                                    </div>
                                  )}
                                  <div>
                                    <p className="font-semibold text-slate-800 text-xs">
                                      {review.reviewer_name ||
                                        review.reviewer_username ||
                                        "—"}
                                    </p>
                                    {review.reviewer_email && (
                                      <p className="text-[11px] text-slate-400">
                                        {review.reviewer_email}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="px-5 py-3.5 font-medium text-slate-700 whitespace-nowrap">
                                {review.rating != null ? (
                                  <span className="inline-flex items-center gap-1 text-amber-500 font-bold">
                                    ★ {review.rating}/5
                                  </span>
                                ) : (
                                  "—"
                                )}
                              </td>
                              <td
                                className="px-5 py-3.5 max-w-xs text-xs text-slate-600 truncate"
                                title={review.comment || ""}
                              >
                                {review.comment || "—"}
                              </td>
                              <td className="px-5 py-3.5 text-xs text-slate-500 whitespace-nowrap">
                                {review.created_at ? (
                                  <DateDisplay
                                    date={new Date(review.created_at)}
                                  />
                                ) : (
                                  "—"
                                )}
                              </td>
                              <td className="px-5 py-3.5 text-right whitespace-nowrap">
                                <div className="inline-flex items-center gap-2">
                                  <button
                                    type="button"
                                    disabled={actioningId === review.id}
                                    onClick={() =>
                                      handleApproveReject(review.id, "approved")
                                    }
                                    className="px-3 py-1 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-lg transition-colors disabled:opacity-50"
                                  >
                                    Approve
                                  </button>
                                  <button
                                    type="button"
                                    disabled={actioningId === review.id}
                                    onClick={() =>
                                      handleApproveReject(review.id, "rejected")
                                    }
                                    className="px-3 py-1 text-xs font-semibold text-rose-700 bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded-lg transition-colors disabled:opacity-50"
                                  >
                                    Reject
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
